import { computed, readonly, ref } from "vue";
import type { RouteLocationNormalized } from "vue-router";
import { getAuthToken } from "@/modules/auth/services/auth.storage";

type RecentSearch = {
  label: string;
  path: string;
};

const STORAGE_KEY = "recent_searches";
const LIMIT = 10;
const searches = ref<RecentSearch[] | null>(null);

function hasLocalStorage() {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

function isLoggedIn() {
  if (!hasLocalStorage()) return false;
  return Boolean(getAuthToken());
}

function load() {
  if (searches.value !== null || !hasLocalStorage()) return;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    // Normalize legacy string arrays into objects
    searches.value = Array.isArray(parsed)
      ? parsed
          .map((item: any) => {
            if (item && typeof item === "object" && "label" in item && "path" in item) {
              return { label: String(item.label), path: String(item.path || "") };
            }
            if (typeof item === "string") {
              return { label: item, path: "" };
            }
            return null;
          })
          .filter(Boolean) as RecentSearch[]
      : [];
  } catch (err) {
    console.warn("Failed to parse recent searches", err);
    searches.value = [];
  }
}

function persist() {
  if (!hasLocalStorage()) return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(searches.value || []));
  } catch (err) {
    console.warn("Failed to save recent searches", err);
  }
}

function addSearch(label?: string | null, path?: string | null) {
  load();
  if (!label) return;
  const trimmed = String(label).trim();
  const resolvedPath = path ? String(path).trim() : "";
  if (!trimmed) return;
  const list = searches.value || [];
  const filtered = list.filter((item) => item.label !== trimmed && item.path !== resolvedPath);
  filtered.unshift({ label: trimmed, path: resolvedPath });
  searches.value = filtered.slice(0, LIMIT);
  persist();
}

function addSearchFromRoute(to: RouteLocationNormalized) {
  if (to.name === "marketplace.home") return;
  if (to.matched.some((record) => record.meta && (record.meta as any).requiresAuth)) return;

  let label =
    to.name === "marketplace.pest-control.vendor"
      ? (to.query?.vendor_name as string | undefined) ||
        (to.params?.vendor_name as string | undefined) ||
        (to.params?.vendor_code as string | undefined)
      : undefined;

  if (!label) {
    label = (to.meta as any)?.title ?? to.name ?? to.fullPath;
  }

  const path = to.fullPath;
  addSearch(label as string | undefined, path);
}

function clear() {
  load();
  searches.value = [];
  if (hasLocalStorage()) localStorage.removeItem(STORAGE_KEY);
}
function useRecentSearches() {
  load();
  return {
    searches: computed(() => readonly(searches.value || [])),
    addSearch,
    addSearchFromRoute,
    clear,
  };
}

export { useRecentSearches, addSearch, addSearchFromRoute, clear };
